/**
 * sota-watch — poll GitHub's public search API for direct-cluster competitors
 * (repos combining AGENTS.md / CLAUDE.md with drift detection and MCP).
 *
 * Design notes:
 * - Deterministic: no AI, just a deterministic HTTP fetch + parse.
 * - Idempotent: caches results to .cai/.cache/sota-watch.json for 24h so
 *   repeated runs within the TTL window return identical data without
 *   consuming GitHub's 60 req/hour unauthenticated rate limit.
 * - Returns top 10 repos sorted by stars (desc) — the same order GitHub returns.
 *
 * The cache file is gitignored and lives next to other CAI telemetry caches.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";

export interface Competitor {
  repo: string;
  stars: number;
  pushedAt: string;
}

export interface SotaWatchResult {
  competitors: Competitor[];
}

export interface SotaWatchCache {
  fetchedAt: number;
  competitors: Competitor[];
}

export interface RunSotaWatchOptions {
  /** Override the cache TTL in ms (default 24h). */
  ttlMs?: number;
  /** Override the project root (default process.cwd()). */
  projectRoot?: string;
  /** Override the fetch implementation (default global.fetch). Useful for tests. */
  fetcher?: typeof fetch;
  /** Override "now" for deterministic TTL tests. */
  now?: () => number;
}

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_DIR = join(".cai", ".cache");
const CACHE_FILE = "sota-watch.json";
const TOP_N = 10;

/** Search query: AGENTS.md OR CLAUDE.md combined with drift detection, TS only. */
const GITHUB_SEARCH_URL =
  "https://api.github.com/search/repositories?q=AGENTS.md+OR+CLAUDE.md+drift+language:typescript&sort=stars&order=desc";

interface GitHubRepo {
  full_name?: unknown;
  stargazers_count?: unknown;
  pushed_at?: unknown;
}

interface GitHubSearchResponse {
  items?: unknown;
}

function isCompetitor(value: unknown): value is Competitor {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.repo === "string" &&
    typeof v.stars === "number" &&
    typeof v.pushedAt === "string"
  );
}

function isSotaWatchCache(value: unknown): value is SotaWatchCache {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.fetchedAt !== "number") return false;
  if (!Array.isArray(v.competitors)) return false;
  return v.competitors.every(isCompetitor);
}

/**
 * Parse a GitHub search response into the top N competitors.
 * Public for unit testing.
 */
export function parseGitHubSearchResponse(raw: unknown): Competitor[] {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("GitHub response is not an object");
  }
  const response = raw as GitHubSearchResponse;
  if (!Array.isArray(response.items)) {
    throw new Error("GitHub response missing items[]");
  }

  const out: Competitor[] = [];
  for (const item of response.items) {
    if (out.length >= TOP_N) break;
    if (typeof item !== "object" || item === null) continue;
    const repo = item as GitHubRepo;
    if (typeof repo.full_name !== "string") continue;
    if (typeof repo.stargazers_count !== "number") continue;
    if (typeof repo.pushed_at !== "string") continue;
    out.push({
      repo: repo.full_name,
      stars: repo.stargazers_count,
      pushedAt: repo.pushed_at,
    });
  }
  return out;
}

function cachePath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, CACHE_FILE);
}

function readCache(path: string): SotaWatchCache | null {
  if (!existsSync(path)) return null;
  try {
    const raw = JSON.parse(readFileSync(path, "utf8")) as unknown;
    if (isSotaWatchCache(raw)) return raw;
    return null;
  } catch {
    return null;
  }
}

function writeCache(path: string, cache: SotaWatchCache): void {
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, JSON.stringify(cache, null, 2), "utf8");
}

/**
 * Run the GitHub sota-watch query.
 *
 * Returns the top 10 direct-cluster competitors. When a fresh-enough cache
 * exists, returns cached data without making a network call.
 *
 * Network failures and non-OK HTTP responses fall back to stale cache
 * when one exists; otherwise they bubble up so the CLI can surface the error.
 */
export async function runSotaWatch(
  options: RunSotaWatchOptions = {}
): Promise<SotaWatchResult> {
  const projectRoot = options.projectRoot ?? process.cwd();
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const now = options.now ?? Date.now;
  const fetcher = options.fetcher ?? fetch;
  const path = cachePath(projectRoot);

  const cached = readCache(path);
  if (cached && now() - cached.fetchedAt < ttlMs) {
    return { competitors: cached.competitors };
  }

  let response: Response;
  try {
    response = await fetcher(GITHUB_SEARCH_URL, {
      headers: { Accept: "application/vnd.github+json" },
    });
  } catch (err) {
    if (cached) return { competitors: cached.competitors };
    throw err instanceof Error ? err : new Error(String(err));
  }

  if (!response.ok) {
    if (cached) return { competitors: cached.competitors };
    throw new Error(
      `GitHub search returned HTTP ${response.status} ${response.statusText}`
    );
  }

  const raw = (await response.json()) as unknown;
  const competitors = parseGitHubSearchResponse(raw);
  writeCache(path, { fetchedAt: now(), competitors });
  return { competitors };
}